import React, { useContext, useEffect, useState, useCallback, useRef } from "react";
import * as Sentry from "@sentry/react";
import { useMutation } from "@apollo/client";
import { UPDATE_UMS_CUSTOMER } from "src/api/customer";
import { ICustomerCopyJob, ICustomerDetail } from "src/stores/CustomerDetailPageStore";
import { UpdateCustomer, UpdateCustomerVariables } from "src/api/generated/UpdateCustomer";
import { UI } from "@wwimmo/ui";
import { Form } from "react-bootstrap";
import { InfoContainer } from "./info-container/InfoContainer";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { Link, useHistory } from "react-router-dom";
import { RootStoreContext } from "src/stores/RootStore";
import * as Screens from "src/screens";
import ImagePlaceholder from "src/assets/images/image_placeholder.svg";
import CustomerLoginInfo from "src/screens/dashboard/customers/customer/login-info/CustomerLoginInfo";
import { getCustomerHostname } from "src/utils/Customer";
import { NetworkConfig } from "src/network/NetworkConfig";
import { MessageType } from "src/components/notifications/Notifier";
import { CustomerDetailsForm } from "src/screens/dashboard/customers/customer/details-form/CustomerDetailsForm";
import { getRoleKey, Role as CustomerRole } from "src/network/User";
import { observer } from "mobx-react-lite";
import { Route as CustomerRoutes, selectRoute } from "src/config/routes";
import CustomerFileUsage from "src/screens/dashboard/customers/customer/file-usage/CustomerFileUsage";
import CustomerAdminuserInfo from "src/screens/dashboard/customers/customer/adminuser-info/CustomerAdminuserInfo";
import { CustomerType, ErpType } from "src/utils/Enums";
import { getFormattedVersionNumber } from "src/utils/Common";
import { ConnectionList } from "src/screens/dashboard/customers/customer/connection/list/ConnectionList";
import { CustomerERPConnectionInfo } from "src/screens/dashboard/customers/customer/erpconnectioninfo/CustomerERPConnectionInfo";
import { CustomerDesign } from "src/screens/dashboard/customers/customer/design/CustomerDesign";
import styles from "./CustomerDetail.module.css";
import { CalculateTilesForm } from "src/screens/dashboard/customers/overview/additional-actions/CalculateTilesForm";
import ManagerUser from "./manage-user/ManageUser";
import InviteUser from "./invite-user/InviteUser";
import { v4 as uuidv4 } from "uuid";

interface Params {
    customerid: string;
}

export interface CustomerDetailsType {
    active: boolean;
    email: string | null;
    erp: number;
    id: string;
    iscopied: boolean;
    name: string | null;
    number: string | null;
    subdomain: string;
    type: number;
    features: number[];
    dns: boolean;
    remarks: string | null;
    partner: number;
    erpusername: string;
    erpcloud: boolean;
}

interface CustomerStatisticsType {
    internalUsers: number | null | undefined;
    externalUsers: number | null | undefined;
    countRealestates: number | null | undefined;
}

interface ICopyErrorDisplay {
    summary: string;
    technicalDetails: string | null;
}

type DatabaseDownloadStatus = "to-start" | "created" | "running" | "completed" | "failed";

interface DatabaseDownloadJob {
    jobId: string;
    status: string;
    timestamp: string;
    error: string | null;
    artifacts: string[];
    storageExplorerPath: string;
}

const DATABASE_DOWNLOAD_STALE_AFTER_DAYS = 7;

const isDatabaseDownloadJobTimestampRecent = (timestamp: string, days: number) =>
    Date.now() - new Date(timestamp).getTime() < days * 24 * 60 * 60 * 1000;

const isDatabaseDownloadJobTimestampToday = (timestamp: string) => {
    const jobDate = new Date(timestamp);
    const now = new Date();

    return (
        jobDate.getFullYear() === now.getFullYear() &&
        jobDate.getMonth() === now.getMonth() &&
        jobDate.getDate() === now.getDate()
    );
};

const DATABASE_DOWNLOAD_BUTTON_TEXT: Record<DatabaseDownloadStatus, string> = {
    "to-start": "Herunterladen starten",
    created: "Wird heruntergeladen",
    running: "Wird heruntergeladen",
    completed: "Öffnen",
    failed: "Datenbank-Erstellung fehlgeschlagen"
};

const isDatabaseDownloadJobInProgress = (status: DatabaseDownloadStatus | undefined) =>
    status === "created" || status === "running";

const DOWNLOAD_DATABASE_JOB_FINISHED_MESSAGE: Record<string, string> = {
    completed: "Der Datenbank-Export wurde erfolgreich abgeschlossen",
    failed: "Beim Datenbank-Export ist ein Fehler aufgetreten"
};

const DOWNLOAD_DATABASE_JOB_FINISHED_MESSAGE_TYPE: Record<string, MessageType> = {
    completed: MessageType.SUCCESS,
    failed: MessageType.ERROR
};

type DatabaseDownloadArtifactsSelection = "database" | "filearchive" | "both";

const DATABASE_DOWNLOAD_ARTIFACTS_BY_SELECTION: Record<DatabaseDownloadArtifactsSelection, string[]> = {
    database: ["Database"],
    filearchive: ["FileArchive"],
    both: ["Database", "FileArchive"]
};

const getDownloadDatabaseErpSubdomain = (erp: number): string => {
    switch (erp) {
        case ErpType["Rimo R5"]:
            return "rimo";
        case ErpType.ImmoTop2:
            return "it2";
        default:
            return "unknown";
    }
};

// Temporary workaround: it2 prod does not follow the "{erp}-{env}" host pattern used by rimo,
// it is served at the plain "exchange.it2.wwimmo.net" instead. To be resolved in the future.
const getDownloadDatabaseErpEnvSegment = (erp: number): string => {
    const erpSubdomain = getDownloadDatabaseErpSubdomain(erp);
    const environment = NetworkConfig.environment;

    if (erp === ErpType.ImmoTop2 && environment === "prod") {
        return erpSubdomain;
    }

    return `${erpSubdomain}-${environment}`;
};

const resolveDownloadDatabaseUrl = (template: string, erp: number, replacements: Record<string, string> = {}) =>
    Object.entries({
        erpEnv: getDownloadDatabaseErpEnvSegment(erp),
        ...replacements
    }).reduce((url, [key, value]) => url.replace(`{${key}}`, value), template);

const buildDownloadDatabaseExistsUrl = (customerNumber: string, erp: number) =>
    resolveDownloadDatabaseUrl(NetworkConfig.downloadDatabaseExistsUrl, erp, { customerNumber });

const buildDownloadDatabaseStatusUrl = (customerNumber: string, jobId: string, erp: number) =>
    resolveDownloadDatabaseUrl(NetworkConfig.downloadDatabaseStatusUrl, erp, { customerNumber, jobId });

const buildDownloadDatabaseStartUrl = (erp: number) => resolveDownloadDatabaseUrl(NetworkConfig.downloadDatabaseStartUrl, erp);

const buildUploadDatabaseDataPendingUrl = (customerNumber: string, erp: number) =>
    resolveDownloadDatabaseUrl(NetworkConfig.uploadDatabaseDataPendingUrl, erp, { customerNumber });

const buildUploadDatabaseDataStartUrl = (erp: number) =>
    resolveDownloadDatabaseUrl(NetworkConfig.uploadDatabaseDataStartUrl, erp);

const buildUploadDatabaseDataStatusUrl = (customerNumber: string, jobId: string, erp: number) =>
    resolveDownloadDatabaseUrl(NetworkConfig.uploadDatabaseDataStatusUrl, erp, { customerNumber, jobId });

const isUploadDatabaseJobFinished = (status: string | undefined) =>
    status === "completed" || status === "failed" || status === "rejected" || status === "canceled";

const UPLOAD_DATABASE_JOB_FINISHED_MESSAGE: Record<string, string> = {
    rejected: "Job wurde abgelehnt, da andere Dateien im Zielverzeichnis schon existieren",
    completed: "Job wurde erfolgreich abgeschlossen, Dateien wurden im Zielverzeichnis hochgeladen",
    failed: "Problem beim Dateien hochzuladen"
};

const UPLOAD_DATABASE_JOB_FINISHED_MESSAGE_TYPE: Record<string, MessageType> = {
    rejected: MessageType.ERROR,
    completed: MessageType.SUCCESS,
    failed: MessageType.ERROR
};

// The database export service expects the customer number without leading zeros (e.g. "10668" instead of "010668")
const stripDownloadDatabaseCustomerNumberLeadingZeros = (customerNumber: string): string =>
    customerNumber.replace(/^0+(?=\d)/, "");

export enum ACTION {
    NONE = 0,
    RESETTING = 1,
    DELETING = 2,
    ACTUALIZING_REPX = 3,
    CLEANING_FILES = 4,
    RESETTING_DESIGN = 5,
    ACTIVATING_DNS = 6,
    DEACTIVATING_DNS = 7,
    ACTUALIZE_ZIPCODES = 8
}

export enum MODAL {
    NONE = 0,
    RESET = 1,
    DELETE = 2,
    ACTUALIZE_REPX = 3,
    CLEAN_FILES = 4,
    RESET_DESIGN = 5,
    ACTIVATE_DNS = 6,
    DEACTIVATE_DNS = 7
}

interface NewCustomerERPCredentials {
    hostname: string;
    username: string;
    password: string;
    title: string;
}

export const formatCopyErrorDisplay = (message: string): ICopyErrorDisplay => {
    const normalizedMessage = message.replace(/\s+/g, " ").trim();
    const firstStackTraceIndex = normalizedMessage.indexOf(" at ");

    if (firstStackTraceIndex === -1) {
        return {
            summary: normalizedMessage,
            technicalDetails: null
        };
    }

    return {
        summary: normalizedMessage.slice(0, firstStackTraceIndex).trim(),
        technicalDetails: normalizedMessage.slice(firstStackTraceIndex).trim()
    };
};

export const isCopyJobRunning = (status: number | null | undefined): boolean => {
    return status !== undefined && status !== null && status !== 2 && status !== 3;
};

const CustomerDetailBase = () => {
    const history = useHistory();
    const { t } = useTranslation();
    const { navStore, uiStore, authStore, customerCopyJobsStore, customerDetailPageStore } =
        useContext(RootStoreContext);

    const { customerid } = useParams<Params>();

    const [customerDetails, setCustomerDetails] = useState<CustomerDetailsType | null>(null);
    const [customerHostname, setCustomerHostname] = useState<string | null>(null);
    const [customerStatistics, setCustomerStatistics] = useState<CustomerStatisticsType | null>(null);
    const [wasCustomerDeleted, setWasCustomerDeleted] = useState(false);
    const [customerLogo, setCustomerLogo] = useState<string>(ImagePlaceholder);
    const [showCopyErrorDetails, setShowCopyErrorDetails] = useState(false);

    const [newCustomerERPCredentials, setNewCustomerERPCredentials] = useState<NewCustomerERPCredentials | undefined>();
    const [databaseDownloadStatus, setDatabaseDownloadStatus] = useState<DatabaseDownloadStatus>("to-start");
    const [activeDatabaseDownloadJobId, setActiveDatabaseDownloadJobId] = useState<string | null>(null);
    const [isCheckingDatabaseDownload, setIsCheckingDatabaseDownload] = useState(true);
    const [completedDatabaseDownloadStorageExplorerPath, setCompletedDatabaseDownloadStorageExplorerPath] =
        useState<string | null>(null);
    const [isDatabaseDownloadArtifactsModalOpen, setIsDatabaseDownloadArtifactsModalOpen] = useState(false);
    const [selectedDatabaseDownloadArtifacts, setSelectedDatabaseDownloadArtifacts] =
        useState<DatabaseDownloadArtifactsSelection>("database");
    const [isStartingDatabaseDownload, setIsStartingDatabaseDownload] = useState(false);
    const [isNewestDatabaseDownloadJobFromToday, setIsNewestDatabaseDownloadJobFromToday] = useState(false);
    const [isUploadDatabaseDataAvailable, setIsUploadDatabaseDataAvailable] = useState(false);
    const [activeUploadDatabaseJobId, setActiveUploadDatabaseJobId] = useState<string | null>(null);

    const [deactivateCustomerMutation] = useMutation<UpdateCustomer, UpdateCustomerVariables>(UPDATE_UMS_CUSTOMER, {
        update: (cache, { data }) => {
            const deletedId = data?.update_ums_customers?.returning[0].id;

            cache.modify({
                fields: {
                    ums_customers(existingCustomerRefs, { readField }) {
                        return existingCustomerRefs.filter(
                            (existingCustomerRef: any) => deletedId !== readField("id", existingCustomerRef)
                        );
                    }
                }
            });
        }
    });

    const deleteCustomer = useCallback(async () => {
        if (authStore.user && authStore.token && customerDetails?.id) {
            const accessToken = authStore.token;
            const tokenType = authStore.tokenType;
            const role = authStore.isSupportUserInRole2ndLevel ? CustomerRole.CUSTOMER2NDLEVEL : authStore.user?.role;

            try {
                const fetchResult = await fetch(`${NetworkConfig.customerBaseUrl}/${customerDetails?.id}`, {
                    method: "DELETE",
                    headers: {
                        Authorization: `${tokenType} ${accessToken}`,
                        "x-hasura-role": getRoleKey(role)
                    }
                });

                if (fetchResult.status === 200) {
                    uiStore.printStatusMessage("Der Kunde wurde erfolgreich gelöscht", MessageType.INFO);
                    uiStore.setHasNewCustomerData(true);
                    setWasCustomerDeleted(true);
                    history.replace(CustomerRoutes.customers);
                } else {
                    uiStore.printStatusMessage(
                        "Beim Löschen ist ein Fehler aufgetreten. Versuche es bitte noch einmal.",
                        MessageType.ERROR
                    );
                }
            } catch (fetchError) {
                uiStore.printStatusMessage(
                    `Beim Löschen des Kunden ist ein Fehler aufgetreten: ${fetchError.error}`,
                    MessageType.ERROR
                );
            }
        }
    }, [
        authStore.user,
        authStore.token,
        authStore.tokenType,
        customerDetails?.id,
        authStore.isSupportUserInRole2ndLevel,
        uiStore,
        history
    ]);

    useEffect(() => {
        if (navStore.back && navStore.back.to.includes("/customers")) {
            navStore.setTitle("Kunden");
            navStore.setBackbutton("/customers", "Kunden");
        }
    }, [navStore]);

    const { loading, error, detailPage } = customerDetailPageStore;
    const refetchCustomerData = customerDetailPageStore.reload;

    useEffect(() => {
        void customerDetailPageStore.load(customerid);

        return () => {
            customerDetailPageStore.reset();
        };
    }, [customerDetailPageStore, customerid]);

    // Die Kopieraufträge stammen aus derselben Antwort. Live gehalten werden sie weiterhin über
    // die Hasura-Subscription — die lässt sich durch einen Endpoint nicht ersetzen.
    useEffect(() => {
        // Beim Wechsel auf einen anderen Kunden liegen kurz noch die Aufträge des vorherigen vor;
        // die dürfen weder angezeigt noch für ein Abo des neuen Kunden herangezogen werden.
        if (detailPage?.customer.id !== customerid) {
            customerCopyJobsStore.reset();
            return;
        }

        customerCopyJobsStore.initializeCopyJobs(customerid, detailPage.copyJobs);

        return () => {
            customerCopyJobsStore.reset();
        };
    }, [customerCopyJobsStore, customerid, detailPage]);

    useEffect(() => {
        if (uiStore.hasNewCustomerData) {
            refetchCustomerData();
            uiStore.setHasNewCustomerData(false);
        }

        if (uiStore.hasNewConnectionData) {
            refetchCustomerData();
            uiStore.setHasNewConnectionData(false);
        }
    }, [refetchCustomerData, uiStore, uiStore.hasNewCustomerData]);

    const setCustomerDetailStates = useCallback(
        (loadedCustomer: ICustomerDetail) => {
            const featuresArray = loadedCustomer.features;

            const erpUserName =
                loadedCustomer.erpUsers.length > 0 && loadedCustomer.erpUsers[0].name
                    ? loadedCustomer.erpUsers[0].name
                    : "";

            if (loadedCustomer.erpUsers.length === 0 && loadedCustomer.erpcloud) {
                uiStore.printStatusMessage("Dieser Kunde hat keinen ERP-Benutzer!", MessageType.ERROR);
            }

            const customerDetails: CustomerDetailsType = {
                active: loadedCustomer.active,
                email: loadedCustomer.email,
                erp: loadedCustomer.erp,
                id: loadedCustomer.id,
                name: loadedCustomer.name,
                number: loadedCustomer.number,
                subdomain: loadedCustomer.subdomain,
                type: loadedCustomer.type,
                features: featuresArray,
                dns: loadedCustomer.dns,
                remarks: loadedCustomer.remarks,
                partner: loadedCustomer.partner,
                erpusername: erpUserName,
                erpcloud: loadedCustomer.erpcloud,
                iscopied: loadedCustomer.isCopied
            };

            setCustomerDetails({
                ...customerDetails,
                features: [...featuresArray]
            });

            setCustomerStatistics({
                internalUsers: loadedCustomer.internalUserCount,
                externalUsers: loadedCustomer.externalUserCount,
                countRealestates: loadedCustomer.realEstateCount
            });

            const hostname = getCustomerHostname(
                customerDetails.subdomain,
                customerDetails.partner,
                customerDetails.dns
            );
            setCustomerHostname(hostname);
        },
        [uiStore]
    );

    useEffect(() => {
        if (!wasCustomerDeleted) {
            navStore.setTitle(t("common.customer"));

            if (detailPage?.customer) {
                navStore.setTitle(t("common.customer") + ": " + detailPage.customer.name);
                setCustomerDetailStates(detailPage.customer);
            }
        }
    }, [detailPage, navStore, t, setCustomerDetailStates, wasCustomerDeleted]);

    useEffect(() => {
        if (customerHostname) {
            const logoUrl = `${customerHostname}/api/designs/logo/brand.jpg?ts=${Date.now()}`;
            setCustomerLogo(logoUrl);
        }
    }, [customerHostname]);

    const onCustomerLogoError = useCallback(() => {
        if (customerLogo !== ImagePlaceholder) {
            setCustomerLogo(ImagePlaceholder);
        }
    }, [customerLogo]);

    const toggleCopyErrorDetails = useCallback(() => {
        setShowCopyErrorDetails((currentValue) => !currentValue);
    }, []);

    const onDeleteCustomer = useCallback(async () => {
        let latestCopyJob: ICustomerCopyJob | null;

        try {
            latestCopyJob = await customerCopyJobsStore.fetchLatestCopyJob(customerid);
        } catch {
            // Ohne aktuellen Kopierstatus lässt sich ein laufender Kopiervorgang nicht ausschliessen.
            uiStore.printStatusMessage(
                t("screens.dashboard.copy_customer.errors.delete_copy_status_unknown"),
                MessageType.ERROR
            );
            return;
        }

        const isCustomerCopyRunning = isCopyJobRunning(latestCopyJob?.status);

        if (isCustomerCopyRunning) {
            uiStore.printStatusMessage(
                t("screens.dashboard.copy_customer.errors.delete_running"),
                MessageType.ERROR
            );
            return;
        }

        if (authStore.isSupportUserInRole2ndLevel && customerDetails?.id && !customerDetails?.iscopied) {
            uiStore.printStatusMessage(
                t("screens.dashboard.copy_customer.errors.delete_not_copied"),
                MessageType.ERROR
            );
        } else {
            await deactivateCustomerMutation({
                variables: {
                    customerid: customerid,
                    updatedFields: {
                        active: false
                    }
                }
            });

            await deleteCustomer();
        }
    }, [
        customerDetails?.id,
        customerDetails?.iscopied,
        customerid,
        deactivateCustomerMutation,
        deleteCustomer,
        customerCopyJobsStore,
        authStore.isSupportUserInRole2ndLevel,
        uiStore,
        t
    ]);

    const callCustomerService = useCallback(
        async (
            serviceEndpointUrl: string,
            httpMethod: string,
            successMessage: string,
            errorMessage: string,
            body: string = ""
        ): Promise<Boolean> => {
            if (authStore.user && authStore.token) {
                const accessToken = authStore.token;
                const tokenType = authStore.tokenType;
                const role = authStore.isSupportUserInRole2ndLevel
                    ? CustomerRole.CUSTOMER2NDLEVEL
                    : authStore.user?.role;

                try {
                    const fetchResult = await fetch(serviceEndpointUrl, {
                        method: httpMethod,
                        body: body,
                        headers: {
                            Authorization: `${tokenType} ${accessToken}`,
                            "x-hasura-role": getRoleKey(role),
                            ...(body ? { "Content-Type": "application/json" } : {})
                        }
                    });

                    if (fetchResult.status === 200 || fetchResult.status === 202) {
                        uiStore.printStatusMessage(successMessage, MessageType.INFO);
                        return true;
                    } else {
                        const response = await fetchResult.json();
                        uiStore.printStatusMessage(`${errorMessage}: ${response.message}`, MessageType.ERROR);
                        return false;
                    }
                } catch (fetchError) {
                    uiStore.printStatusMessage(`${errorMessage}: ${fetchError}`, MessageType.ERROR);
                    return false;
                }
            } else {
                uiStore.printStatusMessage(
                    "Keine Authorisierungs-Informationen vorhanden. Bitte lade die Seite neu.",
                    MessageType.ERROR
                );
                return false;
            }
        },
        [authStore.isSupportUserInRole2ndLevel, authStore.token, authStore.tokenType, authStore.user, uiStore]
    );

    const onResetCustomer = useCallback(async () => {
        if (customerDetails?.id) {
            const resetCustomerEndpointUrl = `${NetworkConfig.resetCustomerUrl}/${customerDetails?.id}`;

            await callCustomerService(
                resetCustomerEndpointUrl,
                "PATCH",
                "Der Kunde wurde erfolgreich zurückgesetzt",
                "Beim Zurücksetzen des Kunden ist ein Fehler aufgetreten."
            );

            refetchCustomerData();
        }
    }, [customerDetails?.id, callCustomerService, refetchCustomerData]);

    const onActualizeRepx = useCallback(async () => {
        if (customerDetails?.id) {
            const actualizeRepxEndpointUrl = `${NetworkConfig.actualizeCustomerRepxUrl}/${customerDetails?.id}`;

            await callCustomerService(
                actualizeRepxEndpointUrl,
                "POST",
                "Die Rexp-Datei wurde erfolgreich aktualisiert",
                "Beim Aktualisieren der Repx-Datei ist ein Fehler aufgetreten"
            );
        }
    }, [customerDetails?.id, callCustomerService]);

    const onCleanupFiles = useCallback(async () => {
        if (customerDetails?.id) {
            const cleanupFilesEndpointUrl = `${NetworkConfig.cleanupFilesUrl}/${customerDetails?.id}`;

            await callCustomerService(
                cleanupFilesEndpointUrl,
                "POST",
                "Die Dateibereinigung wurde erfolgreich abgeschlossen.",
                "Bei der Dateibereinigung ist ein Fehler aufgetreten"
            );

            refetchCustomerData();
        }
    }, [customerDetails?.id, refetchCustomerData, callCustomerService]);

    const onResetDesign = useCallback(async () => {
        if (customerDetails?.subdomain) {
            const resetDesignEndpointUrl = `${NetworkConfig.resetDesignUrl}/${customerDetails?.subdomain}`;

            await callCustomerService(
                resetDesignEndpointUrl,
                "POST",
                "Die Design-Dateien wurden erfolgreich zurückgesetzt",
                "Es ist ein Fehler aufgetreten"
            );
        }
    }, [customerDetails?.subdomain, callCustomerService]);

    const onActivateDns = useCallback(async () => {
        if (customerDetails?.id && customerDetails?.subdomain) {
            const activateDnsEndpointUrl = `${NetworkConfig.activateDnsUrl}`;

            const requestBody = {
                customerid: customerDetails?.id,
                subdomain: customerDetails?.subdomain
            };

            const success = await callCustomerService(
                activateDnsEndpointUrl,
                "POST",
                "DNS wurde erfolgreich aktiviert",
                "Es ist ein Fehler aufgetreten",
                JSON.stringify(requestBody)
            );

            if (success) {
                const hostname = getCustomerHostname(customerDetails.subdomain, customerDetails.partner, true);

                setNewCustomerERPCredentials({
                    hostname: hostname,
                    password: "gleich wie bisher",
                    username: customerDetails.erpusername
                        ? customerDetails.erpusername
                        : `erp@${customerDetails.subdomain}.wwportal.ch`,
                    title: "DNS wurde erfolgreich aktiviert"
                });

                navStore.setTitle("DNS aktivieren");
            }

            uiStore.setHasNewCustomerData(true);
        }
    }, [
        uiStore,
        customerDetails?.id,
        customerDetails?.subdomain,
        callCustomerService,
        customerDetails?.partner,
        navStore,
        customerDetails?.erpusername
    ]);

    const onDeactivateDns = useCallback(async () => {
        if (customerDetails?.id && customerDetails?.subdomain) {
            const deactivateDnsEndpointUrl = `${NetworkConfig.deactivateDnsUrl}`;

            const requestBody = {
                customerid: customerDetails?.id,
                subdomain: customerDetails?.subdomain
            };

            const success = await callCustomerService(
                deactivateDnsEndpointUrl,
                "POST",
                "DNS wurde erfolgreich deaktiviert",
                "Es ist ein Fehler aufgetreten",
                JSON.stringify(requestBody)
            );

            if (success) {
                const hostname = getCustomerHostname(customerDetails.subdomain, customerDetails.partner, false);

                setNewCustomerERPCredentials({
                    hostname: hostname,
                    password: "gleich wie bisher",
                    username: customerDetails.erpusername
                        ? customerDetails.erpusername
                        : `erp@${customerDetails.subdomain}.wwportal.ch`,
                    title: "DNS wurde erfolgreich deaktiviert"
                });

                navStore.setTitle("DNS deaktivieren");
            }

            uiStore.setHasNewCustomerData(true);
        }
    }, [
        uiStore,
        customerDetails?.id,
        customerDetails?.subdomain,
        callCustomerService,
        customerDetails?.partner,
        navStore,
        customerDetails?.erpusername
    ]);

    const onUploadDatabase = useCallback(async () => {
        const rawCustomerNumber = customerDetails?.number;
        const erp = customerDetails?.erp;

        if (!rawCustomerNumber || erp === undefined) {
            return;
        }

        const customerNumber = stripDownloadDatabaseCustomerNumberLeadingZeros(rawCustomerNumber);
        const jobId = uuidv4();

        const success = await callCustomerService(
            buildUploadDatabaseDataStartUrl(erp),
            "POST",
            "Der Datenbank-Upload wurde gestartet",
            "Beim Starten des Datenbank-Uploads ist ein Fehler aufgetreten",
            JSON.stringify({
                customerNumber,
                jobId
            })
        );

        if (success) {
            setActiveUploadDatabaseJobId(jobId);
        }
    }, [customerDetails?.number, customerDetails?.erp, callCustomerService]);

    const getDatabaseDownloadAuthHeaders = useCallback(() => {
        if (!authStore.user || !authStore.token) {
            return null;
        }

        const role = authStore.isSupportUserInRole2ndLevel ? CustomerRole.CUSTOMER2NDLEVEL : authStore.user.role;

        return {
            Authorization: `${authStore.tokenType} ${authStore.token}`,
            "x-hasura-role": getRoleKey(role)
        };
    }, [authStore.user, authStore.token, authStore.tokenType, authStore.isSupportUserInRole2ndLevel]);

    const fetchDatabaseDownloadJobStatus = useCallback(
        async (customerNumber: string, jobId: string, erp: number): Promise<DatabaseDownloadStatus | undefined> => {
            const headers = getDatabaseDownloadAuthHeaders();

            if (!headers) {
                return undefined;
            }

            try {
                const fetchResult = await fetch(buildDownloadDatabaseStatusUrl(customerNumber, jobId, erp), {
                    method: "GET",
                    headers
                });

                if (fetchResult.status === 200) {
                    const response: { status: string; storageExplorerPath: string } = await fetchResult.json();

                    if (response.storageExplorerPath) {
                        setCompletedDatabaseDownloadStorageExplorerPath(response.storageExplorerPath);
                    }

                    return response.status.toLowerCase() as DatabaseDownloadStatus;
                }
            } catch (fetchError) {
                // Silent background poll: errors are only reported to Sentry, no UI feedback
                Sentry.captureException(fetchError);
            }

            return undefined;
        },
        [getDatabaseDownloadAuthHeaders]
    );

    // Only the job started by this session itself should trigger a toast -
    // jobs discovered via polling as already running/finished (e.g. started by another session,
    // or after a reload of this page) are reflected in the UI but do not trigger a toast.
    const sessionStartedDownloadJobIdRef = useRef<string | null>(null);

    const notifyDatabaseDownloadJobFinished = useCallback(
        (jobId: string, status: string) => {
            if (jobId !== sessionStartedDownloadJobIdRef.current) {
                return;
            }

            sessionStartedDownloadJobIdRef.current = null;

            const finishedMessage = DOWNLOAD_DATABASE_JOB_FINISHED_MESSAGE[status];

            if (finishedMessage) {
                uiStore.printStatusMessage(finishedMessage, DOWNLOAD_DATABASE_JOB_FINISHED_MESSAGE_TYPE[status] ?? MessageType.INFO);
            }
        },
        [uiStore]
    );

    const fetchUploadDatabaseJobStatus = useCallback(
        async (customerNumber: string, jobId: string, erp: number): Promise<string | undefined> => {
            const headers = getDatabaseDownloadAuthHeaders();

            if (!headers) {
                return undefined;
            }

            try {
                const fetchResult = await fetch(buildUploadDatabaseDataStatusUrl(customerNumber, jobId, erp), {
                    method: "GET",
                    headers
                });

                if (fetchResult.status === 200) {
                    const response: { status: string } = await fetchResult.json();
                    return response.status.toLowerCase();
                }
            } catch (fetchError) {
                // Silent background poll: errors are only reported to Sentry, no UI feedback
                Sentry.captureException(fetchError);
            }

            return undefined;
        },
        [getDatabaseDownloadAuthHeaders]
    );

    const checkDatabaseDownloadExists = useCallback(async (isBackgroundPoll: boolean = false) => {
        const rawCustomerNumber = customerDetails?.number;
        const erp = customerDetails?.erp;
        const headers = getDatabaseDownloadAuthHeaders();

        if (!rawCustomerNumber || erp === undefined || !headers) {
            return;
        }

        const customerNumber = stripDownloadDatabaseCustomerNumberLeadingZeros(rawCustomerNumber);

        if (!isBackgroundPoll) {
            setIsCheckingDatabaseDownload(true);
        }

        try {
            let fetchResult: Response;

            try {
                fetchResult = await fetch(buildDownloadDatabaseExistsUrl(customerNumber, erp), {
                    method: "GET",
                    headers
                });
            } catch (fetchError) {
                // URL unreachable (e.g. endpoint not yet enabled) -> treat like fetchResult.status !== 200
                Sentry.captureException(fetchError);
                return;
            }

            if (fetchResult.status !== 200) {
                return;
            }

            try {
                const response: { jobs: DatabaseDownloadJob[] } = await fetchResult.json();
                const jobs = response.jobs ?? [];

                if (jobs.length === 0) {
                    setActiveDatabaseDownloadJobId(null);
                    setCompletedDatabaseDownloadStorageExplorerPath(null);
                    setIsNewestDatabaseDownloadJobFromToday(false);
                    setDatabaseDownloadStatus("to-start");
                    return;
                }

                const newestJob = [...jobs].sort(
                    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
                )[0];

                if (!isDatabaseDownloadJobTimestampRecent(newestJob.timestamp, DATABASE_DOWNLOAD_STALE_AFTER_DAYS)) {
                    setActiveDatabaseDownloadJobId(null);
                    setCompletedDatabaseDownloadStorageExplorerPath(null);
                    setIsNewestDatabaseDownloadJobFromToday(false);
                    setDatabaseDownloadStatus("to-start");
                    return;
                }

                const newestJobStatus = newestJob.status.toLowerCase() as DatabaseDownloadStatus;

                if (newestJobStatus === "completed") {
                    setActiveDatabaseDownloadJobId(null);
                    setCompletedDatabaseDownloadStorageExplorerPath(newestJob.storageExplorerPath);
                    setIsNewestDatabaseDownloadJobFromToday(isDatabaseDownloadJobTimestampToday(newestJob.timestamp));
                    setDatabaseDownloadStatus("completed");
                    notifyDatabaseDownloadJobFinished(newestJob.jobId, newestJobStatus);
                    return;
                }

                if (isDatabaseDownloadJobInProgress(newestJobStatus)) {
                    setActiveDatabaseDownloadJobId(newestJob.jobId);
                    setCompletedDatabaseDownloadStorageExplorerPath(null);
                    setIsNewestDatabaseDownloadJobFromToday(false);
                    setDatabaseDownloadStatus(newestJobStatus);
                    return;
                }

                setActiveDatabaseDownloadJobId(null);
                setCompletedDatabaseDownloadStorageExplorerPath(null);
                setIsNewestDatabaseDownloadJobFromToday(false);
                setDatabaseDownloadStatus("to-start");
                notifyDatabaseDownloadJobFinished(newestJob.jobId, newestJobStatus);
            } catch (fetchError) {
                Sentry.captureException(fetchError);
            }
        } finally {
            if (!isBackgroundPoll) {
                setIsCheckingDatabaseDownload(false);
            }
        }
    }, [
        customerDetails?.number,
        customerDetails?.erp,
        getDatabaseDownloadAuthHeaders,
        notifyDatabaseDownloadJobFinished
    ]);

    useEffect(() => {
        if (!customerDetails?.erpcloud || !customerDetails?.number) {
            return;
        }

        void checkDatabaseDownloadExists();

        const intervalId = setInterval(() => {
            void checkDatabaseDownloadExists(true);
        }, 3000);

        return () => clearInterval(intervalId);
    }, [customerDetails?.erpcloud, customerDetails?.number, checkDatabaseDownloadExists]);

    const checkUploadDatabaseDataPending = useCallback(async () => {
        const rawCustomerNumber = customerDetails?.number;
        const erp = customerDetails?.erp;
        const headers = getDatabaseDownloadAuthHeaders();

        if (!rawCustomerNumber || erp === undefined || !headers) {
            setIsUploadDatabaseDataAvailable(false);
            return;
        }

        const customerNumber = stripDownloadDatabaseCustomerNumberLeadingZeros(rawCustomerNumber);

        try {
            const fetchResult = await fetch(buildUploadDatabaseDataPendingUrl(customerNumber, erp), {
                method: "GET",
                headers
            });

            if (fetchResult.status !== 200) {
                setIsUploadDatabaseDataAvailable(false);
                return;
            }

            const response: { hasData?: boolean } = await fetchResult.json();
            setIsUploadDatabaseDataAvailable(response?.hasData === true);
        } catch (fetchError) {
            // Silent background poll: errors are only reported to Sentry, no UI feedback
            Sentry.captureException(fetchError);
            setIsUploadDatabaseDataAvailable(false);
        }
    }, [customerDetails?.number, customerDetails?.erp, getDatabaseDownloadAuthHeaders]);

    useEffect(() => {
        if (!customerDetails?.erpcloud || !customerDetails?.number) {
            setIsUploadDatabaseDataAvailable(false);
            return;
        }

        void checkUploadDatabaseDataPending();

        const intervalId = setInterval(() => {
            void checkUploadDatabaseDataPending();
        }, 3000);

        return () => clearInterval(intervalId);
    }, [customerDetails?.erpcloud, customerDetails?.number, checkUploadDatabaseDataPending, isUploadDatabaseDataAvailable]);

    useEffect(() => {
        if (!activeDatabaseDownloadJobId || !customerDetails?.number || customerDetails?.erp === undefined) {
            return;
        }

        const jobId = activeDatabaseDownloadJobId;
        const customerNumber = stripDownloadDatabaseCustomerNumberLeadingZeros(customerDetails.number);
        const erp = customerDetails.erp;

        const intervalId = setInterval(async () => {
            const status = await fetchDatabaseDownloadJobStatus(customerNumber, jobId, erp);

            if (!status || isDatabaseDownloadJobInProgress(status)) {
                return;
            }

            notifyDatabaseDownloadJobFinished(jobId, status);

            if (status === "completed") {
                setActiveDatabaseDownloadJobId(null);
                setDatabaseDownloadStatus("completed");
                return;
            }

            setActiveDatabaseDownloadJobId(null);
            setCompletedDatabaseDownloadStorageExplorerPath(null);
            setDatabaseDownloadStatus("to-start");
        }, 3000);

        return () => clearInterval(intervalId);
    }, [
        activeDatabaseDownloadJobId,
        customerDetails?.number,
        customerDetails?.erp,
        fetchDatabaseDownloadJobStatus,
        notifyDatabaseDownloadJobFinished
    ]);

    useEffect(() => {
        if (!activeUploadDatabaseJobId || !customerDetails?.number || customerDetails?.erp === undefined) {
            return;
        }

        const jobId = activeUploadDatabaseJobId;
        const customerNumber = stripDownloadDatabaseCustomerNumberLeadingZeros(customerDetails.number);
        const erp = customerDetails.erp;

        const intervalId = setInterval(async () => {
            const status = await fetchUploadDatabaseJobStatus(customerNumber, jobId, erp);

            if (!isUploadDatabaseJobFinished(status)) {
                return;
            }

            const finishedMessage = status ? UPLOAD_DATABASE_JOB_FINISHED_MESSAGE[status] : undefined;

            if (finishedMessage) {
                const finishedMessageType = status
                    ? UPLOAD_DATABASE_JOB_FINISHED_MESSAGE_TYPE[status] ?? MessageType.INFO
                    : MessageType.INFO;

                uiStore.printStatusMessage(finishedMessage, finishedMessageType);
            }

            setActiveUploadDatabaseJobId(null);
        }, 2000);

        return () => clearInterval(intervalId);
    }, [activeUploadDatabaseJobId, customerDetails?.number, customerDetails?.erp, fetchUploadDatabaseJobStatus, uiStore]);

    const downloadCompletedDatabaseFile = useCallback((storageExplorerPath: string) => {
        window.open(storageExplorerPath, "_blank", "noopener,noreferrer");
    }, []);

    const onStartDatabaseDownload = useCallback(
        async (artifacts: string[]) => {
            const rawCustomerNumber = customerDetails?.number;
            const erp = customerDetails?.erp;

            if (!rawCustomerNumber || erp === undefined) {
                return;
            }

            const customerNumber = stripDownloadDatabaseCustomerNumberLeadingZeros(rawCustomerNumber);
            const jobId = uuidv4();

            const success = await callCustomerService(
                buildDownloadDatabaseStartUrl(erp),
                "POST",
                "Der Datenbank-Export wurde gestartet",
                "Beim Starten des Datenbank-Exports ist ein Fehler aufgetreten",
                JSON.stringify({
                    customerNumber,
                    jobId,
                    mode: "nightly",
                    artifacts
                })
            );

            if (success) {
                sessionStartedDownloadJobIdRef.current = jobId;
                setDatabaseDownloadStatus("created");
                setActiveDatabaseDownloadJobId(jobId);

                const status = await fetchDatabaseDownloadJobStatus(customerNumber, jobId, erp);

                if (status && !isDatabaseDownloadJobInProgress(status)) {
                    setActiveDatabaseDownloadJobId(null);
                    notifyDatabaseDownloadJobFinished(jobId, status);

                    if (status === "completed") {
                        setDatabaseDownloadStatus("completed");
                    } else {
                        setCompletedDatabaseDownloadStorageExplorerPath(null);
                        setDatabaseDownloadStatus("to-start");
                    }
                } else if (status) {
                    setDatabaseDownloadStatus(status);
                }
            }
        },
        [
            customerDetails?.number,
            customerDetails?.erp,
            callCustomerService,
            fetchDatabaseDownloadJobStatus,
            notifyDatabaseDownloadJobFinished
        ]
    );

    const toggleDatabaseDownloadArtifactsModal = useCallback(() => {
        setIsDatabaseDownloadArtifactsModalOpen((isOpen) => !isOpen);
    }, []);

    const onConfirmDatabaseDownloadArtifactsSelection = useCallback(async () => {
        if (isStartingDatabaseDownload) {
            return;
        }

        setIsStartingDatabaseDownload(true);

        try {
            await onStartDatabaseDownload(DATABASE_DOWNLOAD_ARTIFACTS_BY_SELECTION[selectedDatabaseDownloadArtifacts]);
            setIsDatabaseDownloadArtifactsModalOpen(false);
        } finally {
            setIsStartingDatabaseDownload(false);
        }
    }, [isStartingDatabaseDownload, onStartDatabaseDownload, selectedDatabaseDownloadArtifacts]);

    const onDownloadCompletedDatabase = useCallback(() => {
        if (!completedDatabaseDownloadStorageExplorerPath) {
            return;
        }

        downloadCompletedDatabaseFile(completedDatabaseDownloadStorageExplorerPath);
    }, [completedDatabaseDownloadStorageExplorerPath, downloadCompletedDatabaseFile]);

    const onOpenDatabaseDownloadArtifactsModal = useCallback(() => {
        setIsDatabaseDownloadArtifactsModalOpen(true);
    }, []);

    const emptyNewCustomerERPCredentialsState = useCallback(async () => {
        setNewCustomerERPCredentials(undefined);
        navStore.setTitle(`Kunde: ${customerDetails?.name}`);
    }, [customerDetails?.name, navStore]);

    if (error) {
        return <Screens.Error message={error.message} />;
    }
    if (loading || customerDetails === null) {
        return <Screens.Loading />;
    }
    if (!detailPage) {
        return <Screens.Error message={t("error.nodata")} />;
    }

    const customerStatisticInfos = customerStatistics
        ? [
              { infoHeading: "Interne Benutzer", infoValue: customerStatistics.internalUsers },
              { infoHeading: "Externe Benutzer", infoValue: customerStatistics.externalUsers },
              { infoHeading: "Anzahl Liegenschaften", infoValue: customerStatistics.countRealestates }
          ]
        : null;

    const userRole = authStore.user ? authStore.user.role : undefined;
    const userCustomerNumber = authStore.user ? authStore.user.customerNumber : "";
    const isSupportUserAllowedToEditCustomer =
        userRole === CustomerRole.CUSTOMERSUPPORT && userCustomerNumber === customerDetails.number;
    const latestCopyErrorMessage = customerCopyJobsStore.latestCopyErrorMessage;
    const copyErrorDisplay = latestCopyErrorMessage ? formatCopyErrorDisplay(latestCopyErrorMessage) : null;

    const dialogButtonCssClasses = "w-100 customer-button";

    const customerDeleteDialog = (
        <UI.ConfirmationDialog
            key="customerDeleteDialog"
            buttonText="Kunde löschen"
            modalTitle="Kunde löschen"
            confirmationQuestion="Willst du den Kunden wirklich löschen?"
            inProgressText="Kunde wird gelöscht..."
            onConfirmation={onDeleteCustomer}
            buttonClasses={dialogButtonCssClasses}
            isDangerousOperation
        />
    );

    const customerResetDialog = (
        <UI.ConfirmationDialog
            key="customerResetDialog"
            buttonText="Kunde zurücksetzen"
            modalTitle="Kunde zurücksetzen"
            confirmationQuestion="Willst du den Kunden wirklich zurücksetzen?"
            inProgressText="Kunde wird zurückgesetzt..."
            onConfirmation={onResetCustomer}
            buttonClasses={dialogButtonCssClasses}
            isDangerousOperation
        />
    );

    const customerActualizeRepxDialog = (
        <UI.ConfirmationDialog
            key="customerActualizeRepxDialog"
            buttonText="Repx aktualisieren"
            modalTitle="Repx-Datei aktualisieren"
            confirmationQuestion="Willst du die Repx-Datei wirklich aktualisieren?"
            inProgressText="Rexp-Datei wird aktualisiert..."
            onConfirmation={onActualizeRepx}
            buttonClasses={dialogButtonCssClasses}
            isDangerousOperation
        />
    );

    const customerCleanupFilesDialog = (
        <UI.ConfirmationDialog
            key="customerCleanupFilesDialog"
            buttonText="Dateibereinigung"
            modalTitle="Dateibereinigung"
            confirmationQuestion="Willst du die Dateibereinigung wirklich starten?"
            inProgressText="Dateibereinigung wird ausgeführt..."
            onConfirmation={onCleanupFiles}
            buttonClasses={dialogButtonCssClasses}
            isDangerousOperation
        />
    );

    const customerResetDesignDialog = (
        <UI.ConfirmationDialog
            key="customerResetDesignDialog"
            buttonText="Design zurücksetzen"
            modalTitle="Design zurücksetzen"
            confirmationQuestion="Willst du die Design-Dateien wirklich zurücksetzen?"
            inProgressText="Design-Dateien werden zurückgesetzt..."
            onConfirmation={onResetDesign}
            buttonClasses={dialogButtonCssClasses}
            isDangerousOperation
        />
    );

    const customerActivateDnsDialog = (
        <UI.ConfirmationDialog
            key="customerActivatednsDialog"
            buttonText="DNS aktivieren"
            modalTitle="DNS aktivieren"
            confirmationQuestion="Willst du die DNS-Einstellungen wirklich aktivieren?"
            inProgressText="DNS-Einstellungen werden aktiviert..."
            onConfirmation={onActivateDns}
            buttonClasses={dialogButtonCssClasses}
            isDangerousOperation
        />
    );

    const customerDeactivateDnsDialog = (
        <UI.ConfirmationDialog
            key="customerDeactivatednsDialog"
            buttonText="DNS deaktivieren"
            modalTitle="DNS deaktivieren"
            confirmationQuestion="Willst du die DNS-Einstellungen wirklich deaktivieren?"
            inProgressText="DNS-Einstellungen werden deaktiviert..."
            onConfirmation={onDeactivateDns}
            buttonClasses={dialogButtonCssClasses}
            isDangerousOperation
        />
    );

    const customerStartDatabaseDownloadButton = (
        <UI.Button
            key="customerStartDatabaseDownloadButton"
            label={
                isDatabaseDownloadJobInProgress(databaseDownloadStatus)
                    ? DATABASE_DOWNLOAD_BUTTON_TEXT[databaseDownloadStatus]
                    : DATABASE_DOWNLOAD_BUTTON_TEXT["to-start"]
            }
            className={`${styles.databaseActionButton} ${dialogButtonCssClasses}`}
            disabled={isDatabaseDownloadJobInProgress(databaseDownloadStatus) || isNewestDatabaseDownloadJobFromToday}
            onClick={onOpenDatabaseDownloadArtifactsModal}
        />
    );

    const customerDownloadCompletedDatabaseButton = (
        <UI.Button
            key="customerDownloadCompletedDatabaseButton"
            label={DATABASE_DOWNLOAD_BUTTON_TEXT["completed"]}
            className={`${styles.databaseActionButton} ${dialogButtonCssClasses}`}
            disabled={databaseDownloadStatus !== "completed"}
            onClick={onDownloadCompletedDatabase}
        />
    );

    const customerDownloadDatabaseArtifactsModal = (
        <UI.Modal
            key="customerDownloadDatabaseArtifactsModal"
            backdrop="static"
            title="Datenbank herunterladen"
            show={isDatabaseDownloadArtifactsModalOpen}
            onClose={toggleDatabaseDownloadArtifactsModal}
        >
            <Form.Check
                type="radio"
                id="databaseDownloadArtifacts-database"
                name="databaseDownloadArtifacts"
                label="Nur Datenbank"
                checked={selectedDatabaseDownloadArtifacts === "database"}
                onChange={() => setSelectedDatabaseDownloadArtifacts("database")}
            />
            <Form.Check
                type="radio"
                id="databaseDownloadArtifacts-filearchive"
                name="databaseDownloadArtifacts"
                label="Nur File-Archive"
                checked={selectedDatabaseDownloadArtifacts === "filearchive"}
                onChange={() => setSelectedDatabaseDownloadArtifacts("filearchive")}
            />
            <Form.Check
                type="radio"
                id="databaseDownloadArtifacts-both"
                name="databaseDownloadArtifacts"
                label="Datenbank und File-Archive"
                checked={selectedDatabaseDownloadArtifacts === "both"}
                onChange={() => setSelectedDatabaseDownloadArtifacts("both")}
            />
            <UI.Row className="mt-5">
                <UI.Col xs={6} md={6}>
                    <UI.Button
                        label="Abbrechen"
                        variant="outline-primary"
                        className="w-100"
                        disabled={isStartingDatabaseDownload}
                        onClick={toggleDatabaseDownloadArtifactsModal}
                    />
                </UI.Col>
                <UI.Col xs={6} md={6} className="d-flex justify-content-end">
                    <UI.Button
                        label="Starten"
                        className="w-100 customer-button"
                        variant="danger"
                        disabled={isStartingDatabaseDownload}
                        onClick={onConfirmDatabaseDownloadArtifactsSelection}
                    />
                </UI.Col>
            </UI.Row>
        </UI.Modal>
    );

    const customerUploadDatabaseDialog = (
        <UI.ConfirmationDialog
            key="customerUploadDatabaseDialog"
            buttonText={activeUploadDatabaseJobId ? "Wird hochgeladen" : "Hochladen starten"}
            modalTitle="Hochladen starten"
            confirmationQuestion="Willst du die ERP Data wirklich hochladen?"
            inProgressText="ERP Data werden hochgeladen..."
            onConfirmation={onUploadDatabase}
            buttonClasses={`${styles.databaseActionButton} ${dialogButtonCssClasses}`}
            isButtonDisabled={!isUploadDatabaseDataAvailable || !!activeUploadDatabaseJobId}
        />
    );

    const customerLogLink = <Link to={"logs"}>{"Logs ansehen >"}</Link>;
    const customerLogInfoElement = [{ infoHeading: "Logs", infoValue: customerLogLink }];
    const customerLogCount = detailPage.customer.logCount;

    const isDnsActivated = detailPage.customer.dns;
    const isCustomerProd = detailPage.customer.type === CustomerType.Prod;

    const extraActionButtons = [
        {
            infoHeading: "Zusätzliche Aktionen",
            infoValue: (
                <>
                    {isCustomerProd ? undefined : customerDeleteDialog}
                    {customerResetDialog}
                    {customerActualizeRepxDialog}
                    {customerCleanupFilesDialog}
                    {customerResetDesignDialog}
                    {isDnsActivated ? customerDeactivateDnsDialog : customerActivateDnsDialog}
                    {<CalculateTilesForm customerid={customerDetails.id} />}
                </>
            )
        }
    ];

    const extraDownloadDatabaseActionButtons = [
        {
            infoHeading: "ERP Datenbank/File Archive",
            infoValue: (
                <>
                    {!isCheckingDatabaseDownload ? (
                        <>
                            {customerStartDatabaseDownloadButton}
                            {customerDownloadCompletedDatabaseButton}
                            {customerDownloadDatabaseArtifactsModal}
                        </>
                    ) : undefined}
                    {userRole === CustomerRole.CUSTOMERDEV || authStore.isSupportUserInRole2ndLevel
                        ? customerUploadDatabaseDialog
                        : undefined}
                </>
            )
        }
    ];

    const extraActionButtonsAdmin = [
        {
            infoHeading: "Zusätzliche Aktionen",
            infoValue:
            <>
                {isDnsActivated ? customerDeactivateDnsDialog : customerActivateDnsDialog}        
            </>
        }
    ];

    const extraActionButtonsSupport = [
        {
            infoHeading: "Zusätzliche Aktionen",
            infoValue: (
                <>
                    {isCustomerProd || !authStore.isSupportUserInRole2ndLevel ? undefined : customerDeleteDialog}
                    {customerResetDialog}
                </>
            )
        }
    ];

    const inspectionMinversion =
        detailPage.inspectionVersions.length > 0
            ? detailPage.inspectionVersions.find((version) => version.key === "INSP.MINVERSION")?.value
            : undefined;

    let formattedInspectionMinVersion = undefined;

    if (inspectionMinversion) {
        formattedInspectionMinVersion = getFormattedVersionNumber(inspectionMinversion);
    }

    const inspectionCurrentVersion =
        detailPage.inspectionVersions.length > 0
            ? detailPage.inspectionVersions.find((version) => version.key === "INSP.CURRENTVERSION")?.value
            : undefined;

    let formattedInspectionCurrentVersion = undefined;

    if (inspectionCurrentVersion) {
        formattedInspectionCurrentVersion = getFormattedVersionNumber(inspectionCurrentVersion);
    }

    const versionInfo = [
        { infoHeading: "Inspection Minversion", infoValue: formattedInspectionMinVersion },
        { infoHeading: "Inspection CurrentVersion", infoValue: formattedInspectionCurrentVersion }
    ];

    const adminuser =
        detailPage.customer.adminUsers.length > 0 ? detailPage.customer.adminUsers[0] : undefined;

    const customerInvoicesLink = <Link to={"invoices"}>{t("screens.customer_details.invoices")}</Link>;
    const customerInvoicesInfoElement = [{ infoHeading: "", infoValue: customerInvoicesLink }];
    const customerSettingsLink = <Link to={"customersettings"}>{t("customer_settings.title")}</Link>;
    const customerSettingsInfoElement = [{ infoHeading: "", infoValue: customerSettingsLink }];

    const connections = detailPage.customer.connections;

    const renderCustomerDetails = () => (
        <>
            {latestCopyErrorMessage ? (
                <UI.Row>
                    <UI.Col md={12}>
                        <div className="mb-3">
                            <button
                                type="button"
                                onClick={toggleCopyErrorDetails}
                                className={styles.copyErrorToggle}
                            >
                                <span className={styles.copyErrorToggleContent}>
                                    <span className={styles.copyErrorIcon}>!</span>
                                    <span className={styles.copyErrorToggleText}>
                                        {t("screens.dashboard.copy_customer.copy_error_toggle")}
                                    </span>
                                </span>
                                <span className={styles.copyErrorChevron}>
                                    <UI.Icon
                                        icon={showCopyErrorDetails ? UI.SVGIcon.ChevronDownThin : UI.SVGIcon.ChevronRightThin}
                                    />
                                </span>
                            </button>
                            {showCopyErrorDetails ? (
                                <div className={styles.copyErrorAlert}>
                                    <div className={styles.copyErrorTitle}>
                                        {t("screens.dashboard.copy_customer.copy_error_title")}
                                    </div>
                                    <div className={styles.copyErrorSummary}>{copyErrorDisplay?.summary}</div>
                                    {copyErrorDisplay?.technicalDetails ? (
                                        <div className={styles.copyErrorDetails}>{copyErrorDisplay.technicalDetails}</div>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    </UI.Col>
                </UI.Row>
            ) : null}
            <UI.Row>
                <UI.Col md={9}>
                    <UI.Card>
                        <CustomerDetailsForm
                            customerDetails={customerDetails}
                            userRole={userRole}
                            setNewCustomerERPCredentials={setNewCustomerERPCredentials}
                        />
                    </UI.Card>
                {userRole ? (
                    userRole === CustomerRole.CUSTOMERDEV ||
                    userRole === CustomerRole.CUSTOMERSUPPORT ||
                    userRole === CustomerRole.CUSTOMERADMIN ? (
                        <>
                            <UI.Card title="Verbindungen">
                                <ConnectionList
                                    connections={connections}
                                    types={detailPage.connectionTypes}
                                    categories={detailPage.connectionCategories}
                                />
                            </UI.Card>
                            {customerDetails && customerHostname ? (
                                <CustomerDesign customerId={customerDetails.id} hostname={customerHostname} />
                            ) : undefined}
                        </>
                    ) : undefined
                ) : undefined}
                </UI.Col>
                <UI.Col md={3}>
                <div className={styles.customerLogoContainer}>
                    <img
                        src={customerLogo}
                        onError={onCustomerLogoError}
                        alt="Kundenlogo"
                        className={styles.customerLogoImage}
                    />
                </div>
                {customerStatistics ? <InfoContainer infoItems={customerStatisticInfos} /> : undefined}

                {inspectionMinversion ? (
                    <InfoContainer key="info-container-insp-minversion" infoItems={versionInfo} />
                ) : undefined}

                {detailPage.customer.apiVersion ? (
                    <InfoContainer
                        key="info-container-apiversion"
                        infoItems={[{ infoHeading: "API-Version", infoValue: detailPage.customer.apiVersion }]}
                    />
                ) : undefined}

                <InfoContainer key="invoices-container" infoItems={customerInvoicesInfoElement} />
                <InfoContainer key="customer-settings-container" infoItems={customerSettingsInfoElement} />

                {userRole ? (
                    userRole === CustomerRole.CUSTOMERDEV ||
                    userRole === CustomerRole.CUSTOMERADMIN ||
                    userRole === CustomerRole.CUSTOMERSUPPORT ||
                    isSupportUserAllowedToEditCustomer ? (
                        //Customeradmin added to roles that can see the tile.
                        <>
                            {customerDetails && customerHostname ? (
                                <div className={styles.customerLoginInformation}>
                                    <CustomerLoginInfo
                                        hostname={customerHostname}
                                        username={customerDetails.erpusername}
                                        passwordHidden={true}
                                        userRole={userRole}
                                        customerId={customerDetails.id}
                                        customerEmail={customerDetails.email}
                                        lastSentEmail={detailPage.lastSentEmail}
                                    />
                                </div>
                            ) : undefined}
                        </>
                    ) : undefined
                ) : undefined}
                {userRole ? (
                    userRole === CustomerRole.CUSTOMERDEV || authStore.isSupportUserInRole2ndLevel ? (
                        <div className={styles.customerLoginInformation}>
                            <CustomerAdminuserInfo
                                adminuser={adminuser}
                                userroleEnumerations={detailPage.userRoles}
                                customerId={customerDetails.id}
                            />
                        </div>
                    ) : undefined
                ) : undefined}
                {userRole ? (
                    (userRole === CustomerRole.CUSTOMERSUPPORT && customerDetails.erpcloud) ||
                    userRole === CustomerRole.CUSTOMERDEV ? (
                        <UI.Card title={t("user_import.title")} className={styles.manageUserCard}>
                            <InviteUser customerDetails={customerDetails} />
                            <ManagerUser
                                customerId={customerDetails.id}
                                userImportProcess={customerDetailPageStore.userImportProcess}
                            />
                        </UI.Card>
                    ) : undefined
                ) : undefined}
                {userRole ? (
                    (userRole === CustomerRole.CUSTOMERDEV || userRole === CustomerRole.CUSTOMERSUPPORT) &&
                    customerLogCount > 0 ? (
                        <>
                            <InfoContainer key="info-container-1" infoItems={customerLogInfoElement} />
                        </>
                    ) : undefined
                ) : undefined}
                {userRole ? (
                    userRole === CustomerRole.CUSTOMERDEV ? (
                        <>
                            <CustomerFileUsage
                                customerFileUsage={detailPage.customer.fileUsage}
                                filesWithErrorsCount={detailPage.customer.filesWithErrorCount}
                            />

                            <InfoContainer
                                key="extra-action-buttons"
                                infoItems={extraActionButtons}
                                className={styles.customerActionBtns}
                            />
                        </>
                    ) : undefined
                ) : undefined}
                {(userRole === CustomerRole.CUSTOMERDEV || userRole === CustomerRole.CUSTOMERSUPPORT) &&
                customerDetails.erpcloud ? (
                    <InfoContainer
                        key="extra-download-database-action-buttons"
                        infoItems={extraDownloadDatabaseActionButtons}
                        className={styles.customerActionBtns}
                    />
                ) : undefined}
                {userRole ? (
                    userRole === CustomerRole.CUSTOMERADMIN ? (
                        <InfoContainer
                            key="extra-action-buttons-admin"
                            infoItems={extraActionButtonsAdmin}
                            className={styles.customerActionBtns}
                        />
                    ) : undefined
                ) : undefined}
                {isSupportUserAllowedToEditCustomer || authStore.isSupportUserInRole2ndLevel ? (
                    <InfoContainer
                        key="extra-action-buttons-support"
                        infoItems={extraActionButtonsSupport}
                        className={styles.customerActionBtns}
                    />
                ) : undefined}
                </UI.Col>
            </UI.Row>
        </>
    );

    return (
        <React.Fragment>
            <UI.Container className="pt-4">
                {newCustomerERPCredentials ? (
                    <UI.Row>
                        <UI.Col md={{ span: 6, offset: 3 }}>
                            <CustomerERPConnectionInfo
                                {...newCustomerERPCredentials}
                                okButtonRoute={selectRoute(CustomerRoutes.customerDetailsOverview, null, {
                                    customerid: customerid
                                })}
                                onOkButtonClick={emptyNewCustomerERPCredentialsState}
                            />
                        </UI.Col>
                    </UI.Row>
                ) : (
                    renderCustomerDetails()
                )}
            </UI.Container>
        </React.Fragment>
    );
};

export const CustomerDetail = Sentry.withProfiler(observer(CustomerDetailBase));

